import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFileExport, FaPlus, FaSearch} from "react-icons/fa";
import InputForm from "../../../../components/input/input-form.jsx";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import {AgGridReact} from "ag-grid-react";
import React, {useCallback, useEffect, useRef, useState} from "react";
import AgGridUtils from "../../../../utils/aggrid-utils.js";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import UsageRecordsService from "../../../../services/web/usage-records-service.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../utils/ag-grid-columns-utils.js";
import projectUtils from "../../../../utils/project-utils.js";
import {FaTriangleExclamation} from "react-icons/fa6";
import platePatternUtils from "../../../../utils/plate-pattern-utils.js";
import ShowHideColumnsGrid from "../../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";

const initialFilter = {
    prefix: "",
    plate: "",
    name: "",
    note: "",
    depStart: null,
    depEnd: null,
    retStart: null,
    retEnd: null,
};

const HIDDEN_COLUMNS_STORAGE = "usage-records-hidden-column";

export default function UsageRecord() {
    const navigate = navigateHook();
    const [filter, setFilter] = useState(initialFilter);
    const [columnsDefs ,setColumnDefs] = useState([]);
    const gridRef = useRef(null);
    const gridApi = useRef(null);

    const onGridReady = useCallback((params) => {
        gridApi.current = params.api;
    }, []);

    const { data: record, isLoading } = useQuery({
        queryKey: ['record-list'],
        queryFn: () => UsageRecordsService.list(),
        initialData: [],
    });

    const toggleColumn = useCallback((field) => {
        if (!field) return;

        setColumnDefs((prev) => {
            const next = prev.map((c) =>
                c.field !== field ? c : { ...c, hide: !c.hide }
            );

            const col = next.find((c) => c.field === field);
            const shouldShow = col ? !col.hide : true;

            queueMicrotask(() => {
                gridApi.current?.setColumnsVisible([field], shouldShow);
            });

            return next;
        });
    }, []);

    const formatToCompareDate = (givenDate) => {
        if (!givenDate || typeof givenDate !== 'string' || givenDate.trim() === '') return null;

        const [datePart, timePart] = givenDate.split(' ');
        const [day, month, year] = datePart.split('/');
        const dateObj = new Date(`${year}-${month}-${day} ${timePart === undefined ? "00:00" : timePart}:00`);

        return dateObj.getTime();
    };

    const dateReturnCell = (p) => {
        const isEmUso = !p.data?.date_return;
        if (!isEmUso) return p.data?.date_return || "Em uso";

        return (
            <span className="inline-flex items-center gap-2 text-[#5dacf6]">
                <FaTriangleExclamation/> Em uso
            </span>
        )
    }

    useEffect(() => {
        const columns = [
            {headerName: "Prefixo", field: "prefix", flex: 1, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams, comparator: aggridUtils.lowerUpCaseComparator},
            {headerName: "Placa", field: "plate", flex: 1, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams, valueFormatter: (p) => projectUtils.formatPlate(p.value), comparator: aggridUtils.nullLastComparator},
            {headerName: "Marca", field: "brand", flex: 1, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams, comparator: aggridUtils.nullLastComparator, hide: true},
            {headerName: "Modelo", field: "model", flex: 1, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams, comparator: aggridUtils.nullLastComparator, hide: true},
            {headerName: "Km saída", field: "km_departure", flex: 1, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams, comparator: aggridUtils.nullLastComparator},
            {headerName: "Data saída", field: "date_departure", flex: 1, filter: "agNumberColumnFilter",
                valueGetter: (p) => formatToCompareDate(p.data?.date_departure),
                valueFormatter: (p) => p.data?.date_departure ?? "",
            },
            {headerName: "Km retorno", field: "km_return", flex: 1, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams, comparator: aggridUtils.nullLastComparator},
            {headerName: "Data retorno", field: "date_return", flex: 1, filter: "agNumberColumnFilter",
                valueGetter: (p) => formatToCompareDate(p.data?.date_return),
                valueFormatter: (p) => p.data?.date_return,
                cellRenderer: dateReturnCell,
            },
            {headerName: "Nome de guerra", field: "namewar", flex: 2, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams},
            {headerName: "Nome", field: "name", flex: 2, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams},
            {headerName: "Observação", field: "note", flex: 2, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams, comparator: aggridUtils.nullLastComparator},
        ];
        const hiddenColumns = (() => {
            try {
                const storedColumns = JSON.parse(localStorage.getItem(HIDDEN_COLUMNS_STORAGE));
                return Array.isArray(storedColumns) ? storedColumns : null;
            } catch {
                return null;
            }
        })();

        if (hiddenColumns) {
            columns.forEach((c) => {
                if (c.field) c.hide = hiddenColumns.includes(c.field);
            });
        }

        setColumnDefs(columns);
    }, []);

    const handleClear = () => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    };

    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
    const endOfDay   = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();

    const handleSearch = useCallback(() => {
        const api = gridRef.current?.api || gridApi;
        if (!api) return;

        const model = {};

        ["prefix", "plate", "name", "namewar" ,"note"].forEach((k) => {
            const v = (filter[k] ?? "").trim();
            if (!v) return;
            model[k] = { filterType: "text", type: "contains", filter: v };
        });

        if (filter.depStart || filter.depEnd) {
            const fromDate = filter.depStart ?? filter.depEnd;
            const toDate = filter.depEnd ?? filter.depStart;

            model.date_departure = {
                filterType: "number",
                type: "inRange",
                filter: startOfDay(fromDate),
                filterTo: endOfDay(toDate),
            };
        }

        if (filter.retStart || filter.retEnd) {
            const fromDate = filter.retStart ?? filter.retEnd;
            const toDate = filter.retEnd ?? filter.retStart;

            model.date_return = {
                filterType: "number",
                type: "inRange",
                filter: startOfDay(fromDate),
                filterTo: endOfDay(toDate),
            };
        }

        api.setFilterModel(model);
    }, [filter, gridApi]);

    const exportData = () => {
        const csvMaker = (data) => Object.values(data).join(';');

        const array = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort(({ data }) => {
            const recordArray = {
                "Prefixo": data.prefix,
                "Placa": data.plate?.replace(/(\w{3})(\w{4})/, "$1-$2") ?? "",
                "Marca": data.brand,
                "Modelo": data.model,
                "Km saída": data.km_departure,
                "Data saída": data.date_departure,
                "Km retorno": data.km_return,
                "Data retorno": data.date_return || "Em uso",
                "Nome": data.name,
                "Nome de guerra": data.namewar,
                "Observação": data.note,
            };

             if (!data.total) delete recordArray.Total;
             array.push(recordArray)
        });
        projectUtils.exportCsvData(csvMaker, array);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSearch();
        if (e.key === "Escape") handleClear();
    };

    const onCellClicked = ({ data, event }) => {
        if (!data?.id) return;
        const urlDev = `/usage-record/form/${data.id}`;
        let isBlank = !!event.ctrlKey;
        navigate(urlDev, isBlank);
    }

    const newUsageRecord = () => {
        const urlDev = "/usage-record/form";
        navigate(urlDev);
    };

    return (
        <Template>
            <Template.Header>
                <Template.Title>Registros de uso</Template.Title>
                <div className="flex gap-2">
                    <Button target="success" onClick={exportData}><FaFileExport/> Exportar</Button>
                    <Button onClick={newUsageRecord}><FaPlus/> Adicionar registro</Button>
                </div>
            </Template.Header>
            <Template.Body className="space-y-2 flex flex-col">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4 items-end">
                            <InputForm
                                id="prefix"
                                label="Prefixo"
                                classNameWrapper="flex-1"
                                value={filter.prefix}
                                onChange={({target: {id, value}}) => setFilter(prev => ({ ...prev, [id]: value }))}
                                onKeyDown={handleKeyDown}
                            />
                            <InputForm
                                id="plate"
                                label="Placa"
                                classNameWrapper="flex-1"
                                value={platePatternUtils.formatPlate(filter.plate)}
                                onChange={({ target: { id, value } }) => setFilter((prev => ({ ...prev, [id]: value})))}
                                onKeyDown={handleKeyDown}
                            />
                            <InputForm
                                id="name"
                                label="Nome"
                                classNameWrapper="flex-2"
                                value={filter.name}
                                onChange={({ target: { id, value } }) => setFilter((prev => ({ ...prev, [id]: value})))}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="flex gap-4 pt-3 items-end">
                            <div className="col-span-4 flex items-stretch">
                                <InputDatetime
                                    id="depStart"
                                    label="Saída entre"
                                    selected={filter.depStart}
                                    onChange={(date) => setFilter(prev => ({ ...prev, depStart: date }))}
                                    dateFormat="L"
                                    customIcon
                                    inputClass="!rounded-e-none"
                                    classNameWrapper="flex-1"
                                />

                                <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                                <InputDatetime
                                    id="depEnd"
                                    selected={filter.depEnd}
                                    onChange={(date) => setFilter(prev => ({ ...prev, depEnd: date }))}
                                    dateFormat="L"
                                    inputClass="!rounded-s-none"
                                    classNameWrapper="flex-1"
                                />
                            </div>
                            <div className="col-span-4 flex items-stretch">
                                <InputDatetime
                                    id="retStart"
                                    label="Retorno entre"
                                    selected={filter.retStart}
                                    onChange={(date) =>
                                        setFilter(prev => ({ ...prev, retStart: date }))
                                    }
                                    dateFormat="L"
                                    customIcon
                                    inputClass="!rounded-e-none"
                                    classNameWrapper="w-[50%]"
                                />

                                <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>

                                <InputDatetime
                                    id="retEnd"
                                    selected={filter.retEnd}
                                    onChange={(date) =>
                                        setFilter(prev => ({ ...prev, retEnd: date }))
                                    }
                                    dateFormat="L"
                                    inputClass="!rounded-s-none"
                                    classNameWrapper="flex-1"
                                />
                            </div>
                            <InputForm
                                id="note"
                                label="Observação"
                                classNameWrapper="flex-1"
                                value={filter.note}
                                onChange={({ target: { id, value } }) => setFilter((prev => ({ ...prev, [id]: value})))}
                                onKeyDown={handleKeyDown}
                            />
                            <div className="flex gap-2">
                                <Button title="Limpar" target="primary-outline" onClick={handleClear}>
                                    <FaEraser/>
                                </Button>

                                <Button title="Buscar" onClick={handleSearch}>
                                    <FaSearch/>
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card className="flex-1 min-h-0">
                    <Card.Body className="h-full w-full min-h-0">
                        <div className="h-[calc(100vh-201px)] w-full">
                            <AgGridReact
                                onGridReady={onGridReady}
                                ref={gridRef}
                                rowData={record?.data ?? []}
                                columnDefs={columnsDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus
                                onCellClicked={onCellClicked}
                                gridOptions={{ localeText: AgGridUtils.localization() }}
                                onFilterChanged={AgGridUtils.filterChangedNoRowsOverlay}
                                suppressNoRowsOverlay={isLoading}
                                theme={AgGridUtils.generalTheme()}
                            />
                        </div>
                        <ShowHideColumnsGrid
                            columnDefs={columnsDefs}
                            toggleColumn={toggleColumn}
                            storageItem={HIDDEN_COLUMNS_STORAGE}
                        />
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}